import { useCallback, useEffect, useState } from 'react';
import { BadgePercent, Banknote, CalendarDays, RotateCcw, Save, WalletCards } from 'lucide-react';
import DatePickerInput from '../../components/DatePickerInput';
import ShopSelect from '../../components/ShopSelect';
import { adminApi } from '../../services/api';

const money = (cents) => new Intl.NumberFormat('en-PH', {
  style: 'currency', currency: 'PHP', minimumFractionDigits: 2,
}).format(Number(cents || 0) / 100);

const dateTime = (value) => value
  ? new Intl.DateTimeFormat('en-PH', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
  : '—';

const statusClass = {
  EARNED: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300',
  PENDING: 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300',
  REFUNDED: 'bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300',
  CANCELLED: 'bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
};

export default function AdminCommissionRevenue() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [rate, setRate] = useState('');
  const [filters, setFilters] = useState({ status: '', businessId: '', dateFrom: '', dateTo: '', page: 1 });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminApi.getCommissionRevenue({ ...filters, limit: 20 });
      const next = response.data || response;
      setData(next);
      setRate(String(next.currentRatePercent));
    } catch (err) {
      setError(err?.message || 'Unable to load platform commission revenue.');
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const updateFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value, page: 1 }));

  const saveRate = async (event) => {
    event.preventDefault();
    const value = Number(rate);
    if (!Number.isFinite(value) || value < 2 || value > 8) {
      setError('Commission rate must be between 2% and 8%.');
      return;
    }
    setSaving(true); setError(''); setNotice('');
    try {
      await adminApi.updateCommissionRate(value);
      setNotice(`Commission rate updated to ${value.toFixed(2).replace(/\.00$/, '')}%. Existing transactions were not changed.`);
      await load();
    } catch (err) { setError(err?.message || 'Unable to update the commission rate.'); }
    finally { setSaving(false); }
  };

  const cards = [
    ['Total earned', data?.summary?.totalCommissionCents, WalletCards],
    ['This month', data?.summary?.currentPeriodCents, CalendarDays],
    ['Pending', data?.summary?.pendingCents, Banknote],
    ['Refunded', data?.summary?.refundedCents, RotateCcw],
  ];

  return (
    <div className="space-y-5 text-neutral-900 dark:text-neutral-50">
      <div>
        <h1 className="text-2xl font-bold">Commission Revenue</h1>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">Platform commission on seller-order item subtotals. Delivery fees are not included.</p>
        <p className="mt-1 max-w-3xl text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">Online payments are earned once the payment is confirmed. Cash on delivery stays pending while the Rider holds the cash and is earned only after the Business accepts the Rider's remittance.</p>
      </div>

      {error && <div role="alert" className="rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">{error}</div>}
      {notice && <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">{notice}</div>}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map(([label, value, Icon]) => (
          <div className="theme-surface flex items-center gap-3 p-4" key={label}>
            <div className="rounded-lg bg-neutral-100 p-2.5 dark:bg-neutral-800"><Icon className="h-5 w-5" /></div>
            <div><p className="text-xs font-medium uppercase tracking-wide text-neutral-500">{label}</p><p className="mt-1 text-xl font-bold">{loading ? '…' : money(value)}</p></div>
          </div>
        ))}
      </div>

      <section className="theme-surface grid gap-5 p-5 lg:grid-cols-[1fr_auto] lg:items-end">
        <div>
          <div className="flex items-center gap-2"><BadgePercent className="h-5 w-5" /><h2 className="font-semibold">Platform commission rate</h2></div>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">Applies to payments confirmed and cash collected after the change. Allowed range: 2%–8%.</p>
          <p className="mt-3 text-3xl font-bold">{data ? `${data.currentRatePercent}%` : '—'}</p>
        </div>
        <form onSubmit={saveRate} className="flex items-end gap-2">
          <label className="block"><span className="mb-1 block text-xs font-medium text-neutral-500">New rate (%)</span><input className="theme-input w-32" type="number" min="2" max="8" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} required /></label>
          <button className="theme-btn-primary gap-2" disabled={saving}><Save className="h-4 w-4" />{saving ? 'Saving…' : 'Update rate'}</button>
        </form>
      </section>

      <section className="theme-surface overflow-hidden">
        <div className="grid gap-3 border-b border-neutral-200 p-4 sm:grid-cols-2 xl:grid-cols-4 dark:border-neutral-800">
          <ShopSelect
            value={filters.status}
            onValueChange={(value) => updateFilter('status', value)}
            options={[
              { value: '', label: 'All statuses' },
              { value: 'EARNED', label: 'Earned' },
              { value: 'PENDING', label: 'Pending' },
              { value: 'CANCELLED', label: 'Cancelled' },
              { value: 'REFUNDED', label: 'Refunded' },
            ]}
            ariaLabel="Status"
            triggerClassName="min-h-11 w-full rounded-xl px-3.5 py-2.5 text-sm"
          />
          <ShopSelect
            value={filters.businessId}
            onValueChange={(value) => updateFilter('businessId', value)}
            options={[
              { value: '', label: 'All businesses' },
              ...(data?.businesses || []).map((business) => ({
                value: business.id,
                label: business.business_name,
              })),
            ]}
            ariaLabel="Business"
            triggerClassName="min-h-11 w-full rounded-xl px-3.5 py-2.5 text-sm"
          />
          <DatePickerInput
            value={filters.dateFrom}
            max={filters.dateTo || undefined}
            onValueChange={(value) => updateFilter('dateFrom', value)}
            title="SELECT START DATE"
            submitLabel="APPLY DATE"
            ariaLabel="From date"
            placeholder="From date"
            className="theme-input min-h-11 w-full rounded-xl px-3.5 py-2.5 text-sm"
          />
          <DatePickerInput
            value={filters.dateTo}
            min={filters.dateFrom || undefined}
            onValueChange={(value) => updateFilter('dateTo', value)}
            title="SELECT END DATE"
            submitLabel="APPLY DATE"
            ariaLabel="To date"
            placeholder="To date"
            className="theme-input min-h-11 w-full rounded-xl px-3.5 py-2.5 text-sm"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[1050px] w-full text-left text-sm">
            <thead className="theme-table-head text-xs uppercase tracking-wide text-neutral-500"><tr>{['Business / order', 'Payment', 'Subtotal', 'Rate', 'Commission', 'Business net', 'Status', 'Date'].map((item) => <th className="px-4 py-3 font-semibold" key={item}>{item}</th>)}</tr></thead>
            <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
              {!loading && (data?.transactions || []).map((row) => <tr className="theme-table-row" key={row.id}>
                <td className="px-4 py-3"><div className="font-medium">{row.businessName}</div><div className="text-xs text-neutral-500">{row.orderReference || `Order #${row.orderId}`}</div></td>
                <td className="px-4 py-3"><div>{row.paymentReference || (row.paymentId ? `#${row.paymentId}` : 'COD / internal')}</div><div className="text-xs text-neutral-500">{row.paymentProvider || 'Internal'}</div></td>
                <td className="px-4 py-3 tabular-nums">{money(row.subtotalCents)}</td><td className="px-4 py-3">{row.appliedRatePercent}%</td><td className="px-4 py-3 font-semibold tabular-nums">{money(row.commissionAmountCents)}</td><td className="px-4 py-3 tabular-nums">{money(row.businessNetAmountCents)}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusClass[row.status] || ''}`}>{row.status}</span></td><td className="px-4 py-3 whitespace-nowrap">{dateTime(row.earnedAt)}</td>
              </tr>)}
              {!loading && !(data?.transactions || []).length && <tr><td colSpan="8" className="px-4 py-12 text-center text-neutral-500">No commission transactions match these filters.</td></tr>}
              {loading && <tr><td colSpan="8" className="px-4 py-12 text-center text-neutral-500">Loading revenue…</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-neutral-200 px-4 py-3 text-sm dark:border-neutral-800"><span className="text-neutral-500">{data?.pagination?.total || 0} transactions</span><div className="flex items-center gap-2"><button className="theme-btn-neutral" disabled={(filters.page || 1) <= 1 || loading} onClick={() => setFilters((v) => ({ ...v, page: v.page - 1 }))}>Previous</button><span>Page {data?.pagination?.page || 1} of {data?.pagination?.totalPages || 1}</span><button className="theme-btn-neutral" disabled={(filters.page || 1) >= (data?.pagination?.totalPages || 1) || loading} onClick={() => setFilters((v) => ({ ...v, page: v.page + 1 }))}>Next</button></div></div>
      </section>
    </div>
  );
}
